import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { TokenContext } from '@/Contexts/TokenContext';
import useContact from '@/hooks/useContact';
import useUser from '@/hooks/useUser';
import { ContactProps } from '@/models/Contact';
import { UserProps, UserType, userTypeStringToEnum } from '@/models/User';
import { logError } from '@/utils/errorHandlerUtil';
import { Spinner } from '@/components/common/Spinner';
import UserLoadError from '@/components/common/UserLoadError';

interface Props {
  userCurrent: UserProps;
  admin: boolean;
  currentContact: ContactProps | null;
  userType: UserType | null;
  usersAll: UserProps[] | null;
  usersImpersonate: UserProps[];
  updateUserCurrent: React.Dispatch<React.SetStateAction<UserProps | null>>;
  updateContactId: (contactId: number | null) => void;
  updateCurrentContact: (contact: ContactProps | null) => void;
  updateUserType: (userType: UserType | null) => void;
  updateUsersAll: React.Dispatch<React.SetStateAction<UserProps[]>>;
}

interface ProviderProps {
  children: React.ReactNode;
}

export const UsersContext = createContext<Props>({
  userCurrent: {} as UserProps,
  admin: false,
  currentContact: null,
  userType: null,
  usersAll: [],
  usersImpersonate: [],
  updateUserCurrent: () => {},
  updateContactId: () => {},
  updateCurrentContact: () => {},
  updateUserType: () => {},
  updateUsersAll: () => {},
});

export const UsersProvider = ({ children }: ProviderProps) => {
  const { authResult, b2cId } = useContext(TokenContext);
  const { getAllUsers, getUser } = useUser();
  const { getContact } = useContact();

  const [admin, setAdmin] = useState<boolean>(false);
  const [fetchedCurrentUser, setFetchedCurrentUser] = useState<UserProps | null>(null);
  const [contactId, setContactId] = useState<number | null>(null);
  const [currentContact, setCurrentContact] = useState<ContactProps | null>(null);
  const [userType, setUserType] = useState<UserType | null>(null);
  const [usersAll, setUsersAll] = useState<UserProps[]>([]);
  const [userLoadFailed, setUserLoadFailed] = useState(false);
  const fetchingUsersRef = useRef(false);

  const updateContactId = useCallback((updatedContactId: number | null) => {
    setContactId(updatedContactId);
  }, []);

  const updateCurrentContact = useCallback(
    (contact: ContactProps | null) => {
      if (contact?.id != null && contact.id != contactId) {
        setContactId(contact.id);
      } else {
        setCurrentContact(contact);
      }
    },
    [contactId]
  );

  useEffect(() => {
    if (contactId != null && (currentContact == null || currentContact.id != Number(contactId))) {
      const fetchContact = async () => {
        const c = await getContact(contactId);
        setCurrentContact(c);
      };

      fetchContact().catch((error) => {
        logError('Error fetching contact.', error);
      });
    }
  }, [contactId, currentContact, getContact]);

  useEffect(() => {
    if (authResult?.account?.idTokenClaims != null) {
      const userTypeString = authResult.account.idTokenClaims.extension_userType as string;
      const isAdmin = authResult.account.idTokenClaims.extension_userIsAdmin as boolean;

      const updatedUserType: UserType = userTypeStringToEnum(userTypeString);
      updateUserType(updatedUserType);
      setAdmin(isAdmin);
    }
  }, [authResult]);

  const updateUserType = (updatedUserType: UserType | null) => {
    setUserType(updatedUserType);
  };

  const updateUsersAll = setUsersAll;

  const usersImpersonate: UserProps[] = useMemo(() => {
    return usersAll != null
      ? usersAll.filter((u: UserProps) => {
          return u.userCanImpersonate;
        })
      : [];
  }, [usersAll]);

  // TODO(bug): this effect re-asserts contactId = userCurrent.contactId on every change, which
  // fights updateCurrentContact(contact) for any contact whose id differs from the user's own
  // (updateCurrentContact only sets contactId in that case). E.g. PrimaryContactSection loads the
  // partner's primary contact, then currentContact snaps back to the logged-in user's contact.
  // Expected: only seed contactId when fetchedCurrentUser changes, not whenever contactId changes.
  useEffect(() => {
    if (fetchedCurrentUser?.contactId != null && contactId != fetchedCurrentUser.contactId) {
      updateContactId(fetchedCurrentUser.contactId);
    }
  }, [fetchedCurrentUser, contactId, updateContactId]);

  useEffect(() => {
    if (
      !userLoadFailed &&
      b2cId != null &&
      authResult?.accessToken != null &&
      (fetchedCurrentUser == null || fetchedCurrentUser.b2CId != b2cId)
    ) {
      // getUser logs its own failures and resolves null; show a retry screen rather than logging
      // the user out (users are only signed out after 12h idle — see AuthWrapper).
      getUser(b2cId)
        .then((results) => {
          if (results == null) {
            setUserLoadFailed(true);
            return;
          }
          setFetchedCurrentUser(results);
        })
        .catch((error) => {
          logError('Error fetching current user.', error);
          setUserLoadFailed(true);
        });
    }
  }, [b2cId, authResult?.accessToken, fetchedCurrentUser, getUser, userLoadFailed]);

  useEffect(() => {
    if (
      usersAll.length === 0 &&
      !fetchingUsersRef.current &&
      authResult?.accessToken != null &&
      authResult?.accessToken !== ''
    ) {
      fetchingUsersRef.current = true;
      getAllUsers()
        .then((users) => {
          setUsersAll(users);
        })
        .catch((error) => {
          logError('Error fetching all users.', error);
        });
    }
  }, [getAllUsers, usersAll.length, authResult?.accessToken]);

  const currentUserMissing = fetchedCurrentUser == null;
  const userCurrent = fetchedCurrentUser!;

  return (
    <UsersContext.Provider
      value={{
        userCurrent,
        currentContact,
        userType,
        admin,
        usersAll,
        usersImpersonate,
        updateUserCurrent: setFetchedCurrentUser,
        updateContactId,
        updateCurrentContact,
        updateUserType,
        updateUsersAll,
      }}
    >
      {!currentUserMissing ? (
        children
      ) : userLoadFailed ? (
        <UserLoadError b2cId={b2cId} onRetry={() => setUserLoadFailed(false)} />
      ) : (
        <Spinner />
      )}
    </UsersContext.Provider>
  );
};
